import { Selectable } from './selections'

/** The page number the page-based list endpoints expect for the first page. */
export const FIRST_PAGE = 1

/**
 * Every page that arrives re-renders the whole option list, so the filters ask
 * for a page big enough to be worth a render. Larger pages also mean fewer
 * round trips to reach the same place in a long list.
 */
export const SEARCH_PAGE_SIZE = 50

/**
 * Where to resume a list from. Opaque to the filter: whatever a search function
 * reports as the `next` of one page is handed straight back to it to ask for the
 * following one. The endpoints behind these functions are split between page
 * numbers and cursors, and both page a list just as well.
 */
export type PageToken = string | number

/**
 * One page of search results. `next` resumes the list, or is null once it is
 * exhausted — the same shape both `PageResult` and `CursorResult` use.
 */
export type DynamicSelectPage = {
  results: Selectable[]
  next: PageToken | null
}

export type DynamicSelectFilters = {
  idIn?: string[]
  search?: string
  suspended?: boolean
  page?: PageToken
}

export type DynamicSelectSearchFn = (
  filters: DynamicSelectFilters,
) => Promise<Selectable[] | DynamicSelectPage>

/**
 * A page-based endpoint's page number. A search function only ever gets back the
 * token it last reported, so for these it is always a number; the first call of
 * a list has no token at all.
 */
export const pageNumberOf = (token?: PageToken): number =>
  typeof token === 'number' ? token : FIRST_PAGE

/** A cursor endpoint's cursor. Undefined starts the list from the beginning. */
export const cursorOf = (token?: PageToken): string | undefined =>
  typeof token === 'string' ? token : undefined

/**
 * Search functions backed by something unpaginated (a static list, an endpoint
 * without paging) return a bare array. Treat those as a single final page so
 * consumers only ever handle one shape.
 */
export const toSearchPage = (result: Selectable[] | DynamicSelectPage): DynamicSelectPage =>
  Array.isArray(result) ? { results: result, next: null } : result

/**
 * Upward movement reveals the applied filters immediately, while hiding them
 * takes a slightly firmer downward drag so the header doesn't flicker on
 * trackpad jitter.
 */
const REVEAL_DELTA_PX = 2
const HIDE_DELTA_PX = 4

/**
 * How far the list scrolls with the applied filters still pinned before they
 * collapse. Roughly two option rows: enough that a small nudge to see the next
 * result or two keeps them in place, so they only give up their space once the
 * user is clearly scrolling into the list.
 */
const HIDE_AFTER_PX = 72

export type ScrollIntent = 'reveal' | 'hide' | 'unchanged'

/** A scroll position together with the list geometry it was measured against. */
export type ListMetrics = {
  scrollTop: number
  scrollHeight: number
  clientHeight: number
}

export const NO_METRICS: ListMetrics = { scrollTop: 0, scrollHeight: 0, clientHeight: 0 }

/** Reads a scrolling list's position and geometry off the element itself. */
export const metricsOf = ({ scrollTop, scrollHeight, clientHeight }: HTMLElement): ListMetrics => ({
  scrollTop,
  scrollHeight,
  clientHeight,
})

export const scrollIntent = (current: ListMetrics, previous: ListMetrics): ScrollIntent => {
  // The list also moves without the user asking it to: a page of results is
  // appended, a picked option leaves the list, the applied filters hand back
  // their space. Any of those can leave scrollTop past the end of the new
  // content, and the browser's correction reads exactly like a scroll upwards —
  // which would pop the applied filters back open. Only compare positions
  // measured against the same geometry, and take this event as the new baseline.
  const reflowed =
    current.scrollHeight !== previous.scrollHeight || current.clientHeight !== previous.clientHeight
  if (reflowed) return 'unchanged'

  const delta = current.scrollTop - previous.scrollTop
  if (delta < -REVEAL_DELTA_PX) return 'reveal'
  if (delta > HIDE_DELTA_PX && current.scrollTop > HIDE_AFTER_PX) return 'hide'
  return 'unchanged'
}

/**
 * Below this many rows the whole list renders. Every row is a checkbox and a
 * label, so the cost of rendering them all is linear and only starts to show
 * after a few pages have been scrolled in.
 */
const WINDOW_ABOVE_ROWS = 100

/**
 * The window is grown and shifted a block at a time rather than row by row, so
 * a drag re-renders the list once every block instead of on every frame. The
 * block is also the overscan: a block's worth of rows sits ready either side of
 * what the user can see.
 */
const WINDOW_BLOCK_ROWS = 20

/** The rows to render, as inclusive row indices. */
export type RowWindow = { start: number; end: number }

/** Render everything — the list is short, or a row cannot be measured yet. */
export const ALL_ROWS: RowWindow = { start: 0, end: Number.MAX_SAFE_INTEGER }

export const rowWindowFor = ({
  scrollTop,
  clientHeight,
  rowHeight,
  rowCount,
}: {
  scrollTop: number
  clientHeight: number
  rowHeight: number
  rowCount: number
}): RowWindow => {
  // Without a measured row height there is no way to say which rows the scroll
  // position corresponds to. jsdom and a not-yet-painted list both land here.
  if (rowCount <= WINDOW_ABOVE_ROWS || rowHeight <= 0 || clientHeight <= 0) return ALL_ROWS

  const firstVisible = Math.floor(scrollTop / rowHeight)
  const lastVisible = Math.ceil((scrollTop + clientHeight) / rowHeight)

  return {
    start: Math.max(0, (Math.floor(firstVisible / WINDOW_BLOCK_ROWS) - 1) * WINDOW_BLOCK_ROWS),
    end: Math.min(
      rowCount - 1,
      (Math.ceil(lastVisible / WINDOW_BLOCK_ROWS) + 1) * WINDOW_BLOCK_ROWS,
    ),
  }
}

export const sameRowWindow = (one: RowWindow, two: RowWindow): boolean =>
  one.start === two.start && one.end === two.end

/**
 * Which row of the list each option occupies. An option that renders as
 * something other than a row — a selection shown above the list, the empty-state
 * placeholder — takes no row and reports -1. Windowing works in rows, since rows
 * are what the scroll position measures, so this is what maps an option's index
 * in `options` onto the geometry.
 */
export const rowLayoutOf = <OptionT>(
  options: OptionT[],
  hasNoRow: (option: OptionT) => boolean,
): { rowOfOption: number[]; rowCount: number } => {
  let rows = 0
  const rowOfOption = options.map(option => (hasNoRow(option) ? -1 : rows++))
  return { rowOfOption, rowCount: rows }
}

/** How close to the end of the list the user gets before the next page is fetched. */
const LOAD_MORE_OFFSET_PX = 96

export const isNearListEnd = ({ scrollTop, scrollHeight, clientHeight }: ListMetrics): boolean =>
  scrollHeight - scrollTop - clientHeight <= LOAD_MORE_OFFSET_PX

/**
 * Visible rows that still cannot fill the popup cannot be scrolled, so the next
 * page would never load. Selected matches don't count: they sit in the applied
 * filters, not in the list.
 *
 * The popup is 40vh. Even on a tall monitor that is only a couple of dozen
 * option rows; keep paging until at least this many unselected rows are on
 * screen, or the list is exhausted.
 */
export const FILL_POPUP_MIN_ROWS = 30

export const listNeedsAnotherPage = (visibleRowCount: number): boolean =>
  visibleRowCount < FILL_POPUP_MIN_ROWS
